import { createContext, useContext } from 'react';
// eslint-disable-next-line no-restricted-imports
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  Cancel,
  InternalAxiosRequestConfig,
  isCancel,
} from 'axios';

import type { SupportedLang } from 'app/lang';
import { LanguageStore } from 'app/lang';
import { getUniversalBackendHost } from 'app/lib/get-universal-backend-host';
import { getUniversalUrl } from 'app/lib/get-universal-url';
import { getGaClientId, getGaSessionId } from 'app/packages/analytics';
import type { BrandName } from 'app/packages/brand';
import { allowGoogleTagManager } from 'app/ssr/config';

// ---

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Everything = any;

export type ServiceError = AxiosError;
export type ServiceInstance = AxiosInstance;
export type ServiceResponse<T> = Promise<AxiosResponse<T>>;

export type ServiceRequestConfig<RequestData> = AxiosRequestConfig<RequestData>;
export type ServiceMethodData = { request?: Everything; response?: Everything };
export type ServiceMethod<
  Data extends ServiceMethodData,
  R = ServiceResponse<Data['response']>
> = (
  (url: string, data?: Data['request'], config?: ServiceRequestConfig<Data['request']>) => R
) | (
  (url: string, config?: ServiceRequestConfig<Data['request']>) => R
);

export type ServiceConfig = {
  instance: ServiceInstance;
  brandName: BrandName;
  language: SupportedLang;
  region?: string | null;
};

export type CreateHttpApiService = typeof createHttpApiService;
type CreateHttpApiServiceProps = {
  isSSR?: boolean;
  signal?: AbortController['signal'];
  baseURL?: string;
};

const replaceAxiosConfigUrl = (config: InternalAxiosRequestConfig) => {
  const { url } = config;

  if (url) {
    const realUrl = getUniversalUrl(url);
    config.url = realUrl;
  }

  return config;
};

const addNoCacheHeaders = (config: InternalAxiosRequestConfig) => {
  const { url } = config;

  if (!url || !config.headers) {
    return config;
  }

  if (isBackendApiURL(config.url)) {
    config.headers.Pragma = 'no-cache';
    config.headers['Cache-Control'] = 'no-cache';
    config.withCredentials = true;
  }

  return config;
};

export const addSelectedLocal = (config: InternalAxiosRequestConfig) => {
  if (!config.headers) {
    return config;
  }
  if (isBackendApiURL(config.url)) {
    config.headers['Accept-Language'] = LanguageStore.getLang();
  }

  return config;
};

export const addGaClientId = (config: InternalAxiosRequestConfig) => {
  const gaClientId = getGaClientId();

  if (!config.headers || !gaClientId) {
    return config;
  }

  if (isBackendApiURL(config.url)) {
    config.headers['X-Web-Client-Id'] = getGaClientId();
  }

  return config;
};

export const addGaSessionId = (config: InternalAxiosRequestConfig) => {
  const gaSessionId = getGaSessionId();

  if (!config.headers || !gaSessionId) {
    return config;
  }

  if (isBackendApiURL(config.url)) {
    config.headers['X-Session-Id'] = gaSessionId;
  }

  return config;
};

export const createHttpApiService = (
  {
    signal,
    baseURL,
  }: CreateHttpApiServiceProps = { signal: undefined, baseURL: undefined },
): ServiceInstance => {
  const instance = axios.create({ signal, baseURL });

  instance.interceptors.request.use(replaceAxiosConfigUrl);
  instance.interceptors.request.use(addNoCacheHeaders);
  instance.interceptors.request.use(addSelectedLocal);
  allowGoogleTagManager && instance.interceptors.request.use(addGaClientId);
  allowGoogleTagManager && instance.interceptors.request.use(addGaSessionId);

  return instance;
};

/*
* Используйте этот инстанс, если нужно просто сделать запрос на сервер.
* Если собираетесь обрабатывать отмену запроса (AbortController) или устанавливать какие-то заголовки
* То используйте функцию createHttpApiService.
* */
export const httpApiService = createHttpApiService();

// ---

export const HttpApiServiceContext = createContext<CreateHttpApiService | null>(null);

export const useHttpApiServiceContext = (): CreateHttpApiService => {
  const createInstance = useContext(HttpApiServiceContext);

  if (createInstance === null) {
    throw 'useHttpApiServiceContext must be used within a HttpApiServiceProvider.';
  }

  return createInstance;
};

// ---

export function isHttpApiServiceError(error: unknown): error is AxiosError {
  return error instanceof Object && 'isAxiosError' in error;
}

export function isCancelError(error: unknown): error is Cancel {
  return isCancel(error);
}

function isBackendApiURL(url?: string) {
  if (!url) {
    return false;
  }

  return url.startsWith('/api/') || url.includes(`${getUniversalBackendHost()}/api/`);
}
